import { z } from 'zod'
import { SISTEMA_PLR_OPTIONS } from './sistemaFieldOptions'

export const SAFETY_FUNCTION_FIELD_MAX_LENGTHS = {
  name: 256,
  description: 4000,
  sfid: 256,
  sftype: 1024,
  triggerevent: 1024,
  reaction: 1024,
  safestate: 1024,
  responsetime: 1024,
  opmode: 50,
  sfinterfaces: 2000,
  requestfrequency: 1024,
  priority: 1024,
  plcal: 4,
  plrdet: 18,
} as const

export type SafetyFunctionFormData = {
  name: string
  description: string
  sfid: string
  sftype: string
  triggerevent: string
  reaction: string
  safestate: string
  responsetime: string
  opmode: string
  sfinterfaces: string
  requestfrequency: string
  priority: string
  plcal: string
  plrdet: string
}

export const defaultSafetyFunctionFormData: SafetyFunctionFormData = {
  name: '',
  description: '',
  sfid: '',
  sftype: '',
  triggerevent: '',
  reaction: '',
  safestate: '',
  responsetime: '',
  opmode: '',
  sfinterfaces: '',
  requestfrequency: '',
  priority: '',
  plcal: '',
  plrdet: '',
}

const optionalTrimmedString = (maxLength: number) =>
  z.string().trim().max(maxLength)

const optionalPlcal = z
  .string()
  .trim()
  .max(SAFETY_FUNCTION_FIELD_MAX_LENGTHS.plcal)
  .refine(
    (value) => value === '' || SISTEMA_PLR_OPTIONS.includes(value as never),
    { message: 'Invalid PL value' }
  )

export const safetyFunctionCreateFormSchema = z.object({
  name: z.string().trim().min(1).max(SAFETY_FUNCTION_FIELD_MAX_LENGTHS.name),
  description: optionalTrimmedString(
    SAFETY_FUNCTION_FIELD_MAX_LENGTHS.description
  ),
})

export const safetyFunctionFormSchema = z.object({
  name: z.string().trim().min(1).max(SAFETY_FUNCTION_FIELD_MAX_LENGTHS.name),
  description: optionalTrimmedString(
    SAFETY_FUNCTION_FIELD_MAX_LENGTHS.description
  ),
  sfid: optionalTrimmedString(SAFETY_FUNCTION_FIELD_MAX_LENGTHS.sfid),
  sftype: optionalTrimmedString(SAFETY_FUNCTION_FIELD_MAX_LENGTHS.sftype),
  triggerevent: optionalTrimmedString(
    SAFETY_FUNCTION_FIELD_MAX_LENGTHS.triggerevent
  ),
  reaction: optionalTrimmedString(SAFETY_FUNCTION_FIELD_MAX_LENGTHS.reaction),
  safestate: optionalTrimmedString(SAFETY_FUNCTION_FIELD_MAX_LENGTHS.safestate),
  responsetime: optionalTrimmedString(
    SAFETY_FUNCTION_FIELD_MAX_LENGTHS.responsetime
  ),
  opmode: optionalTrimmedString(SAFETY_FUNCTION_FIELD_MAX_LENGTHS.opmode),
  sfinterfaces: optionalTrimmedString(
    SAFETY_FUNCTION_FIELD_MAX_LENGTHS.sfinterfaces
  ),
  requestfrequency: optionalTrimmedString(
    SAFETY_FUNCTION_FIELD_MAX_LENGTHS.requestfrequency
  ),
  priority: optionalTrimmedString(SAFETY_FUNCTION_FIELD_MAX_LENGTHS.priority),
  plcal: optionalPlcal,
  plrdet: optionalTrimmedString(SAFETY_FUNCTION_FIELD_MAX_LENGTHS.plrdet),
})

export function validateSafetyFunctionCreateForm(data: {
  name: string
  description: string
}) {
  return safetyFunctionCreateFormSchema.safeParse(data)
}

export function validateSafetyFunctionForm(data: SafetyFunctionFormData) {
  return safetyFunctionFormSchema.safeParse(data)
}

export function toSafetyFunctionPayload(data: SafetyFunctionFormData) {
  const trimOrNull = (value: string) => {
    const trimmed = value.trim()
    if (trimmed.length === 0) {
      return null
    }
    return trimmed
  }

  return {
    name: data.name.trim(),
    description: trimOrNull(data.description),
    sfid: trimOrNull(data.sfid),
    sftype: trimOrNull(data.sftype),
    triggerevent: trimOrNull(data.triggerevent),
    reaction: trimOrNull(data.reaction),
    safestate: trimOrNull(data.safestate),
    responsetime: trimOrNull(data.responsetime),
    opmode: trimOrNull(data.opmode),
    sfinterfaces: trimOrNull(data.sfinterfaces),
    requestfrequency: trimOrNull(data.requestfrequency),
    priority: trimOrNull(data.priority),
    plcal: trimOrNull(data.plcal),
    plrdet: trimOrNull(data.plrdet),
  }
}
